// ===========================================
// PLAYER'S GUIDE PAGE
// ===========================================
// Public page (no auth required) for SEO
// Matches dark casino theme
// ===========================================

import { Link } from 'react-router-dom';

export function GuidePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 border-b border-casino-gold-500/30 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <span className="text-2xl font-bold text-casino-gold-400">BUSTED!</span>
            <span className="text-gray-400 text-sm">Blackjack</span>
          </Link>
          <Link
            to="/"
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-casino-gold-400 mb-4">
            Player's Guide
          </h1>
          <p className="text-gray-400 text-lg">
            Everything you need to know to master BUSTED! Blackjack
          </p>
        </div>

        {/* Table of Contents */}
        <nav className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-12">
          <h2 className="text-xl font-bold text-casino-gold-400 mb-4">Table of Contents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { id: 'getting-started', label: '1. Getting Started' },
              { id: 'game-basics', label: '2. Game Basics' },
              { id: 'placing-bets', label: '3. Placing Bets' },
              { id: 'playing-your-hand', label: '4. Playing Your Hand' },
              { id: 'splitting-hands', label: '5. Splitting Hands' },
              { id: 'insurance', label: '6. Insurance' },
              { id: 'hand-outcomes', label: '7. Hand Outcomes' },
              { id: 'timers', label: '8. Timers & Sit-Out System' },
              { id: 'connection', label: '9. Connection & Reconnection' },
              { id: 'daily-chips', label: '10. Daily Chips' },
              { id: 'busted-buddy', label: '11. BUSTED! Buddy' },
              { id: 'tips', label: '12. Tips & Strategy' },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="text-gray-300 hover:text-casino-gold-400 transition-colors"
              >
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Sections */}
        <div className="space-y-16 guide-content">

          {/* Getting Started */}
          <Section id="getting-started" title="Getting Started">
            <h3 className="subsection-title">Creating an Account</h3>
            <p>Sign in using one of the available authentication providers:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Google</li>
              <li>Discord</li>
              <li>X (Twitter)</li>
            </ul>
            <p className="mt-4">
              Upon your first login, you'll receive <Highlight>2,500 starting chips</Highlight> to begin playing.
            </p>

            <h3 className="subsection-title">Joining a Table</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li>From the home screen, click <Highlight>Play Blackjack</Highlight></li>
              <li>Browse available tables in the lobby</li>
              <li>Tables show their current player count and betting limits</li>
              <li>Click a table to join, then select an available seat</li>
              <li>You can also <Highlight>spectate</Highlight> tables without sitting (up to 50 spectators per table)</li>
            </ol>
          </Section>

          {/* Game Basics */}
          <Section id="game-basics" title="Game Basics">
            <h3 className="subsection-title">Objective</h3>
            <p>Beat the dealer by having a hand value closer to 21 without going over (busting).</p>

            <h3 className="subsection-title">Card Values</h3>
            <Table
              headers={['Card', 'Value']}
              rows={[
                ['2-10', 'Face value'],
                ['Jack, Queen, King', '10'],
                ['Ace', '1 or 11'],
              ]}
            />

            <h3 className="subsection-title">Blackjack</h3>
            <p>
              A <Highlight>Blackjack</Highlight> is an Ace plus any 10-value card (10, J, Q, K) dealt as your first two cards.
              Blackjack pays <Highlight>3:2</Highlight> (bet $10, win $15).
            </p>

            <h3 className="subsection-title">House Rules</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>6-deck shoe</strong> with automatic reshuffle when fewer than 75 cards remain</li>
              <li><strong>Dealer stands on all 17s</strong> (including soft 17)</li>
              <li><strong>7 seats</strong> maximum per table</li>
              <li><strong>Minimum bet:</strong> $1</li>
              <li><strong>Maximum bet:</strong> N/A</li>
            </ul>
          </Section>

          {/* Placing Bets */}
          <Section id="placing-bets" title="Placing Bets">
            <h3 className="subsection-title">Building Your Bet</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li>During the betting phase, click chip denominations to add them to your bet</li>
              <li>Available chips: <Highlight>$1, $5, $25, $100, $500, $1,000, $5,000</Highlight></li>
              <li>Click <Highlight>Undo</Highlight> to remove the last chip added</li>
              <li>Click <Highlight>Clear</Highlight> to return all staged chips to your rail</li>
              <li>Click <Highlight>BET</Highlight> to confirm and send chips to the betting area</li>
            </ol>

            <h3 className="subsection-title">Chip Display</h3>
            <p>
              Your bet displays as the actual chips you selected. Five $1 chips will show as five chips stacked together,
              not consolidated into a $5 chip.
            </p>

            <h3 className="subsection-title">Fractional Amounts</h3>
            <p>
              The game supports fractional chip amounts (e.g., $2.50 for surrender payouts or insurance).
              Your balance and payouts may include decimals.
            </p>

            <h3 className="subsection-title">Autobet</h3>
            <p>Enable <Highlight>Autobet</Highlight> to automatically place the same bet each round:</p>
            <ol className="list-decimal list-inside text-gray-300 space-y-1 ml-4 mt-2">
              <li>Stage your desired bet amount</li>
              <li>Toggle Autobet ON</li>
              <li>Your bet will automatically be placed 400ms after each betting phase begins</li>
              <li>Disable Autobet to return to manual betting</li>
            </ol>
            <p className="text-gray-500 text-sm mt-2 italic">
              Note: Autobet automatically disables if your balance falls below your staged bet amount after a hand resolves.
            </p>
          </Section>

          {/* Playing Your Hand */}
          <Section id="playing-your-hand" title="Playing Your Hand">
            <p>Once all bets are placed and cards are dealt, play proceeds clockwise starting from Seat 1.</p>

            <h3 className="subsection-title">Available Actions</h3>
            <Table
              headers={['Action', 'Description']}
              rows={[
                ['Hit', 'Take another card'],
                ['Stand', 'Keep your current hand'],
                ['Double Down', 'Double your bet, receive exactly one more card, then stand'],
                ['Split', 'Split matching cards into two separate hands'],
                ['Surrender', 'Forfeit half your bet and end your hand immediately'],
              ]}
            />

            <h3 className="subsection-title">Your Turn</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>When it's your turn, your seat glows with a pulsing indicator</li>
              <li>Available actions appear as buttons in the control panel</li>
              <li>You have <Highlight>30 seconds</Highlight> to act (20s grace period + 10s visible countdown)</li>
              <li>If time expires, you will <Highlight>auto-stand</Highlight></li>
            </ul>

            <h3 className="subsection-title">Soft Hands</h3>
            <p>
              A "soft" hand contains an Ace counted as 11. Your score displays both values (e.g., "8/18" means you have either 8 or 18).
            </p>
          </Section>

          {/* Splitting Hands */}
          <Section id="splitting-hands" title="Splitting Hands">
            <h3 className="subsection-title">When Can You Split?</h3>
            <p>You can split when your first two cards have the same value (e.g., two 8s, two Kings, or a King and a Queen).</p>

            <h3 className="subsection-title">How Splitting Works</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li>Click <Highlight>Split</Highlight> to divide your hand into two separate hands</li>
              <li>An additional bet equal to your original bet is placed on the new hand</li>
              <li>Each hand receives one additional card</li>
              <li>Play each hand separately, starting with the first</li>
            </ol>

            <h3 className="subsection-title">Split Hand Features</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Up to 4 hands:</strong> You can split up to 3 times if you keep getting pairs, producing a total of up to four hands</li>
              <li><strong>Navigation:</strong> Use the left/right arrows on either side of your hand to navigate between up to four resulting split hands</li>
              <li><strong>Immediate resolution:</strong> If a split hand busts, it resolves immediately</li>
            </ul>

            <h3 className="subsection-title">Split Hand Rules</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>21 on a split hand pays <Highlight>1:1</Highlight> (not 3:2 like natural blackjack)</li>
              <li>You can double down after splitting</li>
              <li>Split Aces receive only one card each</li>
            </ul>
          </Section>

          {/* Insurance */}
          <Section id="insurance" title="Insurance">
            <h3 className="subsection-title">When Is Insurance Offered?</h3>
            <p>Insurance is offered when the dealer's face-up card is an Ace.</p>

            <h3 className="subsection-title">How It Works</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li>An insurance prompt appears with <Highlight>Accept</Highlight> and <Highlight>Decline</Highlight> buttons</li>
              <li>Insurance costs exactly <Highlight>half your original bet</Highlight> (e.g., $2.50 for a $5 bet)</li>
              <li>You have <Highlight>30 seconds</Highlight> to decide (20s grace period + 10s visible countdown)</li>
              <li>If the dealer has blackjack, insurance pays <Highlight>2:1</Highlight></li>
              <li>If the dealer doesn't have blackjack, you lose the insurance bet</li>
            </ol>

            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mt-4">
              <p className="text-gray-400 text-sm">
                <strong className="text-casino-gold-400">Strategy Tip:</strong> Statistically, insurance is generally not a favorable bet.
                Most strategy guides recommend declining insurance unless you're counting cards.
              </p>
            </div>
          </Section>

          {/* Hand Outcomes */}
          <Section id="hand-outcomes" title="Hand Outcomes">
            <Table
              headers={['Outcome', 'Result']}
              rows={[
                ['Win', 'Your hand beats the dealer — you win 1:1'],
                ['Blackjack', 'Natural 21 with first two cards — you win 3:2'],
                ['Lose', "Dealer's hand beats yours — you lose your bet"],
                ['Bust', 'Your hand exceeds 21 — you lose your bet immediately'],
                ['Push', 'Tie with dealer — your bet is returned'],
                ['Surrender', 'You forfeit — half your bet is returned'],
              ]}
            />

            <h3 className="subsection-title">Payout Animations</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Win/Blackjack:</strong> Chips animate from the table to your stack</li>
              <li><strong>Lose/Bust:</strong> Chips animate away from your betting area</li>
              <li><strong>Push:</strong> Chips return to your rail with a distinctive animation</li>
              <li><strong>Split hands:</strong> Each hand resolves sequentially with its own animation</li>
            </ul>
          </Section>

          {/* Timers */}
          <Section id="timers" title="Timers & Sit-Out System">
            <h3 className="subsection-title">Betting Timer</h3>
            <p>After a round ends, you have time to place your bet for the next round:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4 mt-2">
              <li><strong>20-second grace period</strong> (no visible timer)</li>
              <li><strong>10-second countdown</strong> appears if you haven't bet yet</li>
              <li>If time expires without betting, you are <Highlight>sat out</Highlight> for that round</li>
            </ul>

            <h3 className="subsection-title">Sit-Out & Kick System</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>If you miss a betting window, you're automatically sat out for that round</li>
              <li><Highlight>3 consecutive sit-outs</Highlight> results in being kicked from the table</li>
              <li>You'll be returned to the lobby with a notification</li>
            </ul>

            <h3 className="subsection-title">Timer Summary</h3>
            <Table
              headers={['Timer', 'Grace Period', 'Countdown']}
              rows={[
                ['Betting', '20 seconds', '10 seconds'],
                ['Player Turn', '20 seconds', '10 seconds'],
                ['Insurance', '20 seconds', '10 seconds'],
              ]}
            />
          </Section>

          {/* Connection */}
          <Section id="connection" title="Connection & Reconnection">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6">
              <p className="text-gray-300">
                BUSTED! Blackjack is an <Highlight>online multiplayer game</Highlight> that requires a constant internet connection.
                The game cannot be played offline.
              </p>
            </div>

            <h3 className="subsection-title">What Happens When You Disconnect?</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Reconnecting modal appears</strong> — displays "RECONNECTING..." over the game</li>
              <li><strong>Seat is reserved</strong> — your seat remains reserved for 3 hands</li>
              <li><strong>Auto-reconnect</strong> — the game automatically attempts to restore your connection</li>
              <li><strong>State restored</strong> — upon reconnection, all game state is restored</li>
            </ol>

            <h3 className="subsection-title">Disconnection During Your Turn</h3>
            <Table
              headers={['Scenario', 'Behavior']}
              rows={[
                ['Solo at table', 'Game pauses and waits for you to reconnect'],
                ['Other players present', 'You automatically stand to avoid delaying opponents'],
              ]}
            />

            <h3 className="subsection-title">Grace Periods</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li><strong>Seat reservation:</strong> 3 hands</li>
              <li><strong>Spectator timeout:</strong> 30 minutes (then removed from table)</li>
            </ul>

            <h3 className="subsection-title">Reconnection Tips</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Don't refresh the page — the game automatically reconnects</li>
              <li>Check your network connection</li>
              <li>Your bets and chip balance are maintained server-side</li>
            </ul>
          </Section>

          {/* Daily Chips */}
          <Section id="daily-chips" title="Daily Chips">
            <h3 className="subsection-title">Claiming Daily Chips</h3>
            <p>If your balance is running low, you can claim free daily chips:</p>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4 mt-2">
              <li>Go to the home screen</li>
              <li>Click <Highlight>Claim Daily Chips</Highlight> (if visible)</li>
              <li>Receive <Highlight>1,000 bonus chips</Highlight> to keep playing</li>
            </ol>

            <h3 className="subsection-title">Soft Cap</h3>
            <p>
              The daily chips button is hidden when your balance exceeds <Highlight>1,000,000 chips</Highlight>.
              This encourages active play rather than hoarding.
            </p>

            <h3 className="subsection-title">Reset Time</h3>
            <p>Daily chips reset at <Highlight>midnight UTC</Highlight> each day.</p>
          </Section>

          {/* BUSTED! Buddy */}
          <Section id="busted-buddy" title="BUSTED! Buddy">
            <p>
              For players looking to improve their game, <Highlight>BUSTED! Buddy</Highlight> is an optional coaching feature
              that provides real-time strategy recommendations during play.
            </p>

            <h3 className="subsection-title">What It Does</h3>
            <p>When BUSTED! Buddy is active, the optimal action for your current hand glows during your turn:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4 mt-2">
              <li><strong>Basic Strategy</strong> — The mathematically optimal play for every situation</li>
              <li><strong>True Count</strong> — Hi-Lo card counting adapted for multi-deck shoes</li>
              <li><strong>BUSTED! Insights</strong> — Proprietary adjustments that go beyond textbook strategy</li>
            </ul>

            <h3 className="subsection-title">Who It's For</h3>
            <p>
              Whether you're learning the fundamentals or refining advanced techniques, BUSTED! Buddy helps you
              internalize the patterns that separate casual players from seasoned card sharks.
            </p>

            <h3 className="subsection-title">Availability</h3>
            <p>BUSTED! Buddy is a premium feature available in flexible time-based packages:</p>
            <Table
              headers={['Duration', 'Price']}
              rows={[
                ['24 Hours', '$1'],
                ['30 Days', '$5'],
                ['1 Year', '$25'],
              ]}
            />
            <p className="text-gray-400 text-sm mt-2">
              Access BUSTED! Buddy from the side panel while at any table.
            </p>
          </Section>

          {/* Tips */}
          <Section id="tips" title="Tips & Strategy">
            <h3 className="subsection-title">Basic Strategy</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Always stand on 17 or higher</strong></li>
              <li><strong>Always hit on 8 or lower</strong></li>
              <li><strong>Double down on 11</strong> (especially when dealer shows 2-10)</li>
              <li><strong>Split Aces and 8s</strong> — always</li>
              <li><strong>Never split 10s or 5s</strong></li>
              <li><strong>Hit soft 17</strong> (Ace + 6)</li>
              <li><strong>Surrender 16 vs dealer 9, 10, or Ace</strong></li>
            </ol>

            <h3 className="subsection-title">Bankroll Management</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Set a budget for your session</li>
              <li>Don't chase losses</li>
              <li>Take breaks during losing streaks</li>
              <li>Consider using smaller bets when your balance is low</li>
            </ul>

            <h3 className="subsection-title">Table Etiquette</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
              <li>Play at a reasonable pace — other players are waiting</li>
              <li>Use the chat respectfully</li>
              <li>Be patient with new players learning the game</li>
              <li>Don't criticize others' playing decisions</li>
            </ul>
          </Section>

          {/* Quick Reference */}
          <Section id="quick-reference" title="Quick Reference Card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="subsection-title">Actions</h3>
                <Table
                  headers={['Button', 'Action']}
                  rows={[
                    ['Hit', 'Request another card'],
                    ['Stand', 'Keep current hand'],
                    ['Double', 'Double bet, one card, then stand'],
                    ['Split', 'Divide pair into two hands'],
                    ['Surrender', 'Forfeit half bet'],
                  ]}
                />
              </div>
              <div>
                <h3 className="subsection-title">Payouts</h3>
                <Table
                  headers={['Result', 'Payout']}
                  rows={[
                    ['Win', '1:1'],
                    ['Blackjack', '3:2'],
                    ['Insurance (dealer BJ)', '2:1'],
                    ['Push', 'Bet returned'],
                    ['Surrender', 'Half bet returned'],
                  ]}
                />
              </div>
            </div>

            <h3 className="subsection-title mt-6">Limits</h3>
            <Table
              headers={['Limit', 'Value']}
              rows={[
                ['Minimum Bet', '$1'],
                ['Maximum Bet', 'N/A'],
                ['Max Players', '7'],
                ['Max Spectators', '50'],
                ['Max Split Hands', '4'],
              ]}
            />
          </Section>

          {/* Download PDF */}
          <div className="bg-gradient-to-r from-casino-gold-500/20 to-casino-gold-600/20 border border-casino-gold-500/50 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-casino-gold-400 mb-4">Download PDF Version</h2>
            <p className="text-gray-300 mb-6">
              Want to read offline or print a copy? Download the full Player's Guide as a PDF.
            </p>
            <a
              href="/PLAYERS_GUIDE.pdf"
              download="BUSTED_Blackjack_Players_Guide.pdf"
              className="inline-flex items-center gap-2 px-6 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download PDF
            </a>
          </div>

          {/* Need Help */}
          <div className="text-center py-8 border-t border-gray-800">
            <h2 className="text-xl font-bold text-white mb-4">Need Help?</h2>
            <p className="text-gray-400 mb-2">
              <strong>Technical issues:</strong> Report at{' '}
              <a href="mailto:info@bustedblackjack.com" className="text-casino-gold-400 hover:underline">
                info@bustedblackjack.com
              </a>
            </p>
            <p className="text-gray-500 text-sm mt-6 italic">
              BUSTED! Blackjack is a social casino game using play money only. No real gambling. Must be 18+ to play.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

// ===========================================
// HELPER COMPONENTS
// ===========================================

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20">
      <h2 className="text-2xl md:text-3xl font-bold text-casino-gold-400 mb-6 pb-2 border-b border-casino-gold-500/30">
        {title}
      </h2>
      <div className="space-y-4 text-gray-300">{children}</div>
    </section>
  );
}

function Highlight({ children }: { children: React.ReactNode }) {
  return <span className="text-casino-gold-400 font-semibold">{children}</span>;
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-800">
            {headers.map((header, i) => (
              <th key={i} className="px-4 py-2 text-left text-casino-gold-400 font-semibold border border-gray-700">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/50'}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2 text-gray-300 border border-gray-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default GuidePage;
